import React, { useState, useEffect } from 'react';
import { 
  getSystemSettings, 
  updateSystemSettings, 
  runMaintenanceTask,
  uploadLogo,
  getMediaList,
  setActiveMedia,
  deleteMedia,
  initializeDefaultSettings
} from '../../services/api';
import {getImageUrl} from '../../utils/imageUtils';

function SystemSettings({ admin }) {
  const [settings, setSettings] = useState({});
  const [media, setMedia] = useState({
    logos: [],
    favicons: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('platform');
  const [logoPreview, setLogoPreview] = useState('');
  const [faviconPreview, setFaviconPreview] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getSystemSettings();
      
      // Check if response has the expected structure
      if (!response.success) {
        throw new Error(response.message || 'Failed to load settings');
      }
      
      // Format settings for easier access
      const formattedSettings = {};
      Object.keys(response.data.settings || {}).forEach(category => {
        response.data.settings[category].forEach(setting => {
          if (!formattedSettings[category]) {
            formattedSettings[category] = {};
          }
          formattedSettings[category][setting.key] = setting.value;
        });
      });
  
      setSettings(formattedSettings);
      setMedia({
        logos: response.data.media?.logos || [],
        favicons: response.data.media?.favicons || []
      });
  
      // Set preview URLs
      const activeLogo = response.data.media?.logos?.find(logo => logo.is_active);
      const activeFavicon = response.data.media?.favicons?.find(fav => fav.is_active);
      
      if (activeLogo) setLogoPreview(activeLogo.full_url);
      if (activeFavicon) setFaviconPreview(activeFavicon.full_url);
  
    } catch (err) {
      console.error('Error loading settings:', err);
      
      // Handle auth errors specifically
      if (err.message.includes('Authentication failed') || err.message === 'Authentication failed') {
        setError('Your session has expired. Please log in again.');
        // The apiCall will handle redirect automatically
        return;
      }
      
      setError('Failed to load system settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Convert settings to the format expected by the API
      const settingsToUpdate = [];
      Object.keys(settings).forEach(category => {
        Object.keys(settings[category]).forEach(key => {
          settingsToUpdate.push({
            key,
            value: settings[category][key]
          });
        });
      });
  
      const response = await updateSystemSettings(settingsToUpdate);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update settings');
      }
      
      setSuccess('Settings updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      
      // Handle auth errors
      if (err.message.includes('Authentication failed') || err.message === 'Authentication failed') {
        setError('Your session has expired. Please log in again.');
        return;
      }
      
      setError('Failed to update settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };


  const handleRunMaintenance = async (task) => {
    if (!window.confirm(`Run ${task.replace(/_/g, ' ')} task?`)) {
      return;
    }

    try {
      const response = await runMaintenanceTask(task);
      alert(`Task completed: ${response.data.affected_rows || 0} items processed`);
    } catch (err) {
      setError('Failed to run maintenance task: ' + err.message);
    }
  };

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const handleFileUpload = async (event, type) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file size (5MB for logo, 1MB for favicon)
  const maxSize = type === 'logo' ? 5 * 1024 * 1024 : 1 * 1024 * 1024;
  if (file.size > maxSize) {
    setError(`File size exceeds ${type === 'logo' ? '5MB' : '1MB'} limit`);
    return;
  }

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
  if (!allowedTypes.includes(file.type)) {
    setError('Invalid file type. Please upload JPEG, PNG, GIF, or SVG files.');
    return;
  }

  try {
    setUploading(true);
    setError('');

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    if (type === 'logo') {
      setLogoPreview(previewUrl);
    } else {
      setFaviconPreview(previewUrl);
    }

    // Upload file
    const result = await uploadLogo(file, type, {
      title: `${type === 'logo' ? 'Platform Logo' : 'Favicon'}`,
      altText: `${type === 'logo' ? 'Platform Logo' : 'Favicon'}`
    });

    if (!result.success) {
      throw new Error(result.message || 'Upload failed');
    }

    // Refresh media list
    await fetchSettings();
    
    // Set as active
    await setActiveMedia(result.data.media_id);
    
    setSuccess(`${type === 'logo' ? 'Logo' : 'Favicon'} uploaded successfully!`);
    setTimeout(() => setSuccess(''), 3000);

  } catch (err) {
    console.error('Error uploading file:', err);
    
    // Handle auth errors
    if (err.message.includes('Authentication failed') || err.message === 'Authentication failed') {
      setError('Your session has expired. Please log in again.');
      return;
    }
    
    // Clean up preview URL on error
    if (type === 'logo') {
      setLogoPreview('');
    } else {
      setFaviconPreview('');
    }
    
    setError(`Failed to upload ${type}: ` + err.message);
  } finally {
    setUploading(false);
    event.target.value = ''; // Reset file input
  }
};


  const handleSetActiveLogo = async (mediaId) => {
    try {
      await setActiveMedia(mediaId);
      await fetchSettings();
      setSuccess('Logo set as active!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to set active logo: ' + err.message);
    }
  };

  const handleDeleteLogo = async (mediaId, fileName) => {
    if (!window.confirm(`Delete ${fileName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteMedia(mediaId);
      await fetchSettings();
      setSuccess('Logo deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete logo: ' + err.message);
    }
  };

  const handleInitializeDefaults = async () => {
    if (!window.confirm('This will reset all settings to defaults. Continue?')) {
      return;
    }

    try {
      setLoading(true);
      await initializeDefaultSettings();
      await fetchSettings();
      setSuccess('Default settings initialized successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to initialize defaults: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (admin.role !== 'super_admin') {
    return (
      <div className="min-h-full bg-gray-50 p-6">
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🚫</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-500">Only super admins can access system settings.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading system settings...</p>
        </div>
      </div>
    );
  }

  const tabConfig = [
    { id: 'platform', label: 'Platform' },
    { id: 'appearance', label: 'Appearance' },
    { id: 'email', label: 'Email' },
    { id: 'payments', label: 'Payments' },
    { id: 'listings', label: 'Listings' },
    { id: 'security', label: 'Security' },
    { id: 'maintenance', label: 'Maintenance' }
  ];

  const getSettingValue = (category, key, defaultValue = '') => {
    return settings[category]?.[key] ?? defaultValue;
  };

  return (
    <div className="min-h-full bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
            <p className="text-gray-600 mt-1">Configure platform settings and preferences</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleInitializeDefaults}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Reset to Defaults
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-[#ff660e] text-white px-6 py-2 rounded-lg hover:bg-[#e55b0c] disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <span className="text-red-500">⚠️</span>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <span className="text-green-500">✅</span>
            <p className="text-green-700">{success}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Tabs */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex -mb-px min-w-max">
            {tabConfig.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-6 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#ff660e] text-[#ff660e]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Platform Settings */}
          {activeTab === 'platform' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platform Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('platform', 'platform_name', 'EcoMart')}
                    onChange={(e) => handleSettingChange('platform', 'platform_name', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platform Version
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('platform', 'platform_version', '1.0.0')}
                    onChange={(e) => handleSettingChange('platform', 'platform_version', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-300 rounded"
                    checked={getSettingValue('platform', 'maintenance_mode', false)}
                    onChange={(e) => handleSettingChange('platform', 'maintenance_mode', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Maintenance Mode</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-300 rounded"
                    checked={getSettingValue('platform', 'registration_enabled', true)}
                    onChange={(e) => handleSettingChange('platform', 'registration_enabled', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">User Registration Enabled</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-300 rounded"
                    checked={getSettingValue('platform', 'seller_approval_required', true)}
                    onChange={(e) => handleSettingChange('platform', 'seller_approval_required', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Seller Approval Required</span>
                </label>
              </div>
            </div>
          )}

          {/* Appearance Settings */}
          {activeTab === 'appearance' && (
            <div className="space-y-8">
              {/* Logo Upload Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Logo</h3>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-1">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      {logoPreview ? (
                        <div className="space-y-4">
                          <div className="flex justify-center">
                            <img 
                              src={logoPreview} 
                              alt="Logo preview" 
                              className="max-h-40 max-w-full object-contain"
                            />
                          </div>
                          <p className="text-sm text-gray-600">Current logo preview</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="text-gray-400 text-4xl">🏢</div>
                          <p className="text-gray-600">No logo uploaded</p>
                        </div>
                      )}
                      
                      <label className="mt-4 inline-block">
                        <span className="cursor-pointer bg-[#ff660e] text-white px-4 py-2 rounded-lg hover:bg-[#e55b0c] transition-colors">
                          {uploading ? 'Uploading...' : 'Upload New Logo'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'logo')}
                          disabled={uploading}
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-2">
                        Recommended: 300x100px PNG or SVG, max 5MB
                      </p>
                    </div>
                  </div>

                  {/* Logo Gallery */}
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Previous Logos</h4>
                    {media.logos.length > 0 ? (
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {media.logos.map(logo => (
                          <div 
                            key={logo.media_id} 
                            className={`p-3 border rounded-lg flex items-center justify-between ${
                              logo.is_active ? 'border-[#ff660e] bg-orange-50' : 'border-gray-200'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <img 
                                src={getImageUrl(logo.full_url)}
                                alt={logo.title}
                                className="h-10 w-auto object-contain"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{logo.file_name}</p>
                                <p className="text-xs text-gray-500">
                                  {new Date(logo.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {!logo.is_active && (
                                <button
                                  onClick={() => handleSetActiveLogo(logo.media_id)}
                                  className="text-sm text-[#ff660e] hover:text-[#e55b0c]"
                                >
                                  Set Active
                                </button>
                              )}
                              {logo.is_active ? (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                  Active
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleDeleteLogo(logo.media_id, logo.file_name)}
                                  className="text-sm text-red-600 hover:text-red-800"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No previous logos found
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Favicon Upload Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Favicon</h3>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-1">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      {faviconPreview ? (
                        <div className="space-y-4">
                          <div className="flex justify-center">
                            <img 
                              src={faviconPreview} 
                              alt="Favicon preview" 
                              className="h-16 w-16 object-contain"
                            />
                          </div>
                          <p className="text-sm text-gray-600">Current favicon preview</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="text-gray-400 text-4xl">🎯</div>
                          <p className="text-gray-600">No favicon uploaded</p>
                        </div>
                      )}
                      
                      <label className="mt-4 inline-block">
                        <span className="cursor-pointer bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                          {uploading ? 'Uploading...' : 'Upload Favicon'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'favicon')}
                          disabled={uploading}
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-2">
                        Recommended: 32x32px ICO or PNG, max 1MB
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Color Settings */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Colors</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        className="h-10 w-16 cursor-pointer"
                        value={getSettingValue('appearance', 'primary_color', '#007bff')}
                        onChange={(e) => handleSettingChange('appearance', 'primary_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                        value={getSettingValue('appearance', 'primary_color', '#007bff')}
                        onChange={(e) => handleSettingChange('appearance', 'primary_color', e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secondary Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        className="h-10 w-16 cursor-pointer"
                        value={getSettingValue('appearance', 'secondary_color', '#6c757d')}
                        onChange={(e) => handleSettingChange('appearance', 'secondary_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                        value={getSettingValue('appearance', 'secondary_color', '#6c757d')}
                        onChange={(e) => handleSettingChange('appearance', 'secondary_color', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Email Settings */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Email
                  </label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('email', 'from_email', 'noreply@ecomart.com')}
                    onChange={(e) => handleSettingChange('email', 'from_email', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('email', 'from_name', 'EcoMart')}
                    onChange={(e) => handleSettingChange('email', 'from_name', e.target.value)}
                  />
                </div>
              </div>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-300 rounded"
                  checked={getSettingValue('email', 'smtp_enabled', true)}
                  onChange={(e) => handleSettingChange('email', 'smtp_enabled', e.target.checked)}
                />
                <span className="text-sm text-gray-700">SMTP Enabled</span>
              </label>
            </div>
          )}

          {/* Payment Settings */}
          {activeTab === 'payments' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('payments', 'currency', 'NGN')}
                    onChange={(e) => handleSettingChange('payments', 'currency', e.target.value)}
                  >
                    <option value="NGN">NGN - Nigerian Naira</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commission Rate (%)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                      value={getSettingValue('payments', 'commission_rate', 5.0)}
                      onChange={(e) => handleSettingChange('payments', 'commission_rate', parseFloat(e.target.value))}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-gray-500">%</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Payout Amount
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                      value={getSettingValue('payments', 'min_payout_amount', 1000.00)}
                      onChange={(e) => handleSettingChange('payments', 'min_payout_amount', parseFloat(e.target.value))}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-gray-500">
                        {getSettingValue('payments', 'currency', 'NGN')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-300 rounded"
                  checked={getSettingValue('payments', 'auto_payout_enabled', true)}
                  onChange={(e) => handleSettingChange('payments', 'auto_payout_enabled', e.target.checked)}
                />
                <span className="text-sm text-gray-700">Auto Payout Enabled</span>
              </label>
            </div>
          )}

          {/* Listing Settings */}
          {activeTab === 'listings' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Images per Listing
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('listings', 'max_images_per_listing', 10)}
                    onChange={(e) => handleSettingChange('listings', 'max_images_per_listing', parseInt(e.target.value) || 1)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Listing Duration (Days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('listings', 'listing_duration_days', 30)}
                    onChange={(e) => handleSettingChange('listings', 'listing_duration_days', parseInt(e.target.value) || 1)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Active Listings per Seller
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('listings', 'max_active_listings_per_seller', 50)}
                    onChange={(e) => handleSettingChange('listings', 'max_active_listings_per_seller', parseInt(e.target.value) || 1)}
                  />
                </div>
              </div>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-300 rounded"
                  checked={getSettingValue('listings', 'auto_approval', false)}
                  onChange={(e) => handleSettingChange('listings', 'auto_approval', e.target.checked)}
                />
                <span className="text-sm text-gray-700">Auto Approve Listings</span>
              </label>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Login Attempts
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('security', 'max_login_attempts', 5)}
                    onChange={(e) => handleSettingChange('security', 'max_login_attempts', parseInt(e.target.value) || 1)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Timeout (Minutes)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="1440"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('security', 'session_timeout_minutes', 60)}
                    onChange={(e) => handleSettingChange('security', 'session_timeout_minutes', parseInt(e.target.value) || 5)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password Minimum Length
                  </label>
                  <input
                    type="number"
                    min="6"
                    max="32"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    value={getSettingValue('security', 'password_min_length', 8)}
                    onChange={(e) => handleSettingChange('security', 'password_min_length', parseInt(e.target.value) || 6)}
                  />
                </div>
              </div>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-300 rounded"
                  checked={getSettingValue('security', 'require_2fa', false)}
                  onChange={(e) => handleSettingChange('security', 'require_2fa', e.target.checked)}
                />
                <span className="text-sm text-gray-700">Require Two-Factor Authentication</span>
              </label>
            </div>
          )}

          {/* Maintenance Settings */}
          {activeTab === 'maintenance' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">System Maintenance Tasks</h3>
              <p className="text-sm text-gray-600">
                Run maintenance tasks to keep the system optimized and clean.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => handleRunMaintenance('cleanup_expired_listings')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900">Cleanup Expired Listings</div>
                    <span className="text-gray-400 group-hover:text-gray-600">⚡</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Deactivate listings that have passed their expiry date
                  </div>
                </button>

                <button
                  onClick={() => handleRunMaintenance('cleanup_old_logs')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900">Cleanup Old Logs</div>
                    <span className="text-gray-400 group-hover:text-gray-600">🗑️</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Remove admin activity logs older than 90 days
                  </div>
                </button>

                <button
                  onClick={() => handleRunMaintenance('update_statistics')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900">Update Statistics</div>
                    <span className="text-gray-400 group-hover:text-gray-600">📊</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Recalculate seller statistics and performance metrics
                  </div>
                </button>

                <button
                  onClick={() => handleRunMaintenance('backup_database')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900">Backup Database</div>
                    <span className="text-gray-400 group-hover:text-gray-600">💾</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Create a backup of the current database
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SystemSettings;